import { useState } from"react";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import {
 Dialog,
 DialogContent,
 DialogDescription,
 DialogHeader,
 DialogTitle,
} from"@/components/ui/dialog";
import { Loader2, Calendar, CheckCircle2 } from"lucide-react";
import { supabase } from"@/integrations/supabase/client";
import { toast } from"sonner";

interface ScheduleDemoDialogProps {
 open: boolean;
 onOpenChange: (open: boolean) => void;
}

export const ScheduleDemoDialog = ({ open, onOpenChange }: ScheduleDemoDialogProps) => {
 const [name, setName] = useState("");
 const [email, setEmail] = useState("");
 const [company, setCompany] = useState("");
 const [message, setMessage] = useState("");
 const [availableTimes, setAvailableTimes] = useState("");
 const [isSubmitting, setIsSubmitting] = useState(false);
 const [submitted, setSubmitted] = useState(false);

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 if (!name.trim() || !email.trim() || !message.trim() || !availableTimes.trim()) return;

 const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
 if (!emailRegex.test(email.trim())) {
 toast.error("Please enter a valid email address");
 return;
 }

 setIsSubmitting(true);
 try {
 const { error } = await supabase.from("demo_requests" as any).insert({
 name: name.trim().slice(0, 200),
 email: email.trim().slice(0, 255),
 company: company.trim().slice(0, 200) || null,
 message: message.trim().slice(0, 2000),
 available_times: availableTimes.trim().slice(0, 1000),
 } as any);

 if (error) throw error;
 setSubmitted(true);
 toast.success("Demo request submitted! We'll be in touch soon.");
 } catch (error: any) {
 console.error("Demo request error:", error);
 toast.error("Failed to submit request. Please try again.");
 } finally {
 setIsSubmitting(false);
 }
 };

 const handleClose = (val: boolean) => {
 if (!val) {
 setSubmitted(false);
 setName("");
 setEmail("");
 setCompany("");
 setMessage("");
 setAvailableTimes("");
 }
 onOpenChange(val);
 };

 if (submitted) {
 return (
 <Dialog open={open} onOpenChange={handleClose}>
 <DialogContent className="max-w-md">
 <div className="py-8 text-center">
 <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-primary" />
 <h3 className="text-xl font-bold mb-2">Demo Request Received!</h3>
 <p className="text-muted-foreground text-sm">
 We'll review your availability and send you a meeting link shortly. Check your email for the confirmation.
 </p>
 <Button className="mt-6" onClick={() => handleClose(false)}>
 Close
 </Button>
 </div>
 </DialogContent>
 </Dialog>
 );
 }

 return (
 <Dialog open={open} onOpenChange={handleClose}>
 <DialogContent className="max-w-md">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2">
 <Calendar className="h-5 w-5 text-primary" />
 Schedule a Demo
 </DialogTitle>
 <DialogDescription>
 Tell us about your needs and when you're available. We'll send you a meeting link.
 </DialogDescription>
 </DialogHeader>

 <form onSubmit={handleSubmit} className="space-y-4">
 <div className="grid grid-cols-2 gap-3">
 <div className="space-y-1.5">
 <Label htmlFor="demo-name">Name *</Label>
 <Input
 id="demo-name"
 placeholder="Your name"
 value={name}
 onChange={(e) => setName(e.target.value)}
 required
 maxLength={200}
 />
 </div>
 <div className="space-y-1.5">
 <Label htmlFor="demo-email">Email *</Label>
 <Input
 id="demo-email"
 type="email"
 placeholder="you@company.com"
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 required
 maxLength={255}
 />
 </div>
 </div>

 <div className="space-y-1.5">
 <Label htmlFor="demo-company">Company</Label>
 <Input
 id="demo-company"
 placeholder="Your company (optional)"
 value={company}
 onChange={(e) => setCompany(e.target.value)}
 maxLength={200}
 />
 </div>

 <div className="space-y-1.5">
 <Label htmlFor="demo-message">What would you like to see? *</Label>
 <Textarea
 id="demo-message"
 placeholder="Tell us about your use case and what you'd like to explore in the demo..."
 value={message}
 onChange={(e) => setMessage(e.target.value)}
 required
 maxLength={2000}
 rows={3}
 />
 </div>

 <div className="space-y-1.5">
 <Label htmlFor="demo-times">Available Times *</Label>
 <Textarea
 id="demo-times"
 placeholder="e.g., Mon-Wed 10am-2pm GMT, or Thursday afternoon EST..."
 value={availableTimes}
 onChange={(e) => setAvailableTimes(e.target.value)}
 required
 maxLength={1000}
 rows={2}
 />
 </div>

 <Button
 type="submit"
 className="w-full"
 disabled={isSubmitting || !name.trim() || !email.trim() || !message.trim() || !availableTimes.trim()}
 >
 {isSubmitting ? (
 <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</>
 ) : (
"Request Demo"
 )}
 </Button>
 </form>
 </DialogContent>
 </Dialog>
 );
};
